import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import './editors.css';

interface MultiselectEditorProps {
  value: string[];
  options: string[];
  onChange: (values: string[]) => void;
  onClose: () => void;
  allowCreate?: boolean;
}

// Generate consistent color for each option based on hash
const getOptionColor = (option: string): { bg: string; text: string } => {
  const colors = [
    { bg: 'rgba(219, 39, 119, 0.2)', text: '#db2777' },  // pink
    { bg: 'rgba(132, 117, 78, 0.3)', text: '#84754e' },  // olive
    { bg: 'rgba(75, 85, 99, 0.3)', text: '#6b7280' },    // gray
    { bg: 'rgba(59, 130, 246, 0.2)', text: '#3b82f6' },  // blue
    { bg: 'rgba(34, 197, 94, 0.2)', text: '#22c55e' },   // green
    { bg: 'rgba(168, 85, 247, 0.2)', text: '#a855f7' },  // purple
    { bg: 'rgba(249, 115, 22, 0.2)', text: '#f97316' },  // orange
    { bg: 'rgba(236, 72, 153, 0.2)', text: '#ec4899' },  // magenta
  ];

  let hash = 0;
  for (let i = 0; i < option.length; i++) {
    hash = option.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const MultiselectEditor: React.FC<MultiselectEditorProps> = ({
  value,
  options,
  onChange,
  onClose,
  allowCreate = true,
}) => {
  const [searchText, setSearchText] = useState('');
  const [localValues, setLocalValues] = useState<string[]>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        // Save and close
        onChange(localValues);
        onClose();
      }
    };

    // Delay adding listener to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [localValues, onChange, onClose]);

  // Filter options based on search
  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(searchText.toLowerCase()) &&
    !localValues.includes(opt)
  );

  // Check if search text could create a new option
  const canCreateNew = allowCreate &&
    searchText.trim() &&
    !options.some(opt => opt.toLowerCase() === searchText.toLowerCase()) &&
    !localValues.some(v => v.toLowerCase() === searchText.toLowerCase());

  const handleRemoveValue = (val: string) => {
    const newValues = localValues.filter(v => v !== val);
    setLocalValues(newValues);
  };

  const handleAddValue = (val: string) => {
    if (!localValues.includes(val)) {
      const newValues = [...localValues, val];
      setLocalValues(newValues);
      setSearchText('');
    }
  };

  const handleCreateNew = () => {
    if (searchText.trim()) {
      handleAddValue(searchText.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canCreateNew) {
      e.preventDefault();
      handleCreateNew();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onChange(localValues);
      onClose();
    } else if (e.key === 'Backspace' && !searchText && localValues.length > 0) {
      // Remove last selected value when backspace on empty input
      handleRemoveValue(localValues[localValues.length - 1]);
    }
  };

  return (
    <div
      ref={containerRef}
      className="multiselect-editor"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Selected values as pills */}
      <div className="multiselect-editor-input-row">
        {localValues.map((val) => {
          const color = getOptionColor(val);
          return (
            <span
              key={val}
              className="multiselect-editor-pill"
              style={{ backgroundColor: color.bg, color: color.text }}
            >
              {val}
              <button
                className="multiselect-editor-pill-remove"
                onClick={() => handleRemoveValue(val)}
                style={{ color: color.text }}
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          className="multiselect-editor-input"
          placeholder={localValues.length === 0 ? "Search..." : ""}
        />
      </div>

      {/* Hint text */}
      <div className="multiselect-editor-hint">
        Select an option or create one
      </div>

      {/* Options list */}
      <div className="multiselect-editor-options">
        {filteredOptions.map((opt) => {
          const color = getOptionColor(opt);
          return (
            <div
              key={opt}
              className="multiselect-editor-option"
              onClick={() => handleAddValue(opt)}
            >
              <span className="multiselect-editor-option-drag">⋮⋮</span>
              <span
                className="multiselect-editor-option-pill"
                style={{ backgroundColor: color.bg, color: color.text }}
              >
                {opt}
              </span>
            </div>
          );
        })}

        {/* Create new option */}
        {canCreateNew && (
          <div
            className="multiselect-editor-option multiselect-editor-create"
            onClick={handleCreateNew}
          >
            <span className="multiselect-editor-create-text">
              Create "<strong>{searchText}</strong>"
            </span>
          </div>
        )}

        {filteredOptions.length === 0 && !canCreateNew && searchText && (
          <div className="multiselect-editor-empty">
            No options found
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiselectEditor;
